import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Fade,
  Snackbar as MuiSnackbar,
  SnackbarCloseReason,
  SnackbarProps,
} from '@mui/material';
import { noop } from 'utils/noop';
import { Notification, NotificationFunction, NotificationType } from './types';
import { getAlertSeverity } from './utils';

const NOTIFICATION_DURATION = 3000;
const ERROR_NOTIFICATION_DURATION = 8000;

export let notification: NotificationFunction = noop;
export let errorNotification: NotificationFunction = noop;
export let infoNotification: NotificationFunction = noop;
export let warningNotification: NotificationFunction = noop;
export let successNotification: NotificationFunction = noop;
export let closeNotification = noop;

const Snackbar: FC = () => {
  const [open, setOpen] = useState(false);
  const [notificationOptionsPack, setNotificationOptionsPack] = useState<
    Notification[]
  >([]);
  const [notificationOptions, setNotificationOptions] =
    useState<Notification | null>(null);

  useEffect(() => {
    if (notificationOptionsPack.length && !notificationOptions) {
      // Set a new snack when we don't have an active one
      setNotificationOptions({ ...notificationOptionsPack[0] });
      setNotificationOptionsPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (notificationOptionsPack.length && notificationOptions && open) {
      // Close an active snack when a new one is added
      setOpen(false);
    }
  }, [notificationOptionsPack, notificationOptions, open]);

  const handleClose = (
    event?: React.SyntheticEvent<any, any>,
    reason?: SnackbarCloseReason,
  ) => {
    if (notificationOptions?.onClose && event && reason) {
      notificationOptions?.onClose(event, reason);
    }
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleExited = (node: HTMLElement) => {
    if (notificationOptions?.TransitionProps?.onExited) {
      notificationOptions?.TransitionProps?.onExited(node);
    }
    setNotificationOptions(null);
  };

  const updateNotification =
    (type: NotificationType) => (message: string, options?: SnackbarProps) => {
      setNotificationOptionsPack((prevState) => [
        ...prevState,
        { ...options, message, type, key: Date.now() },
      ]);
    };

  notification = updateNotification(NotificationType.DEFAULT);
  successNotification = updateNotification(NotificationType.SUCCESS);
  warningNotification = updateNotification(NotificationType.WARNING);
  infoNotification = updateNotification(NotificationType.INFO);
  errorNotification = updateNotification(NotificationType.ERROR);
  closeNotification = handleClose;

  const isError = notificationOptions?.type === NotificationType.ERROR;

  return (
    <MuiSnackbar
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      open={open}
      TransitionComponent={Fade}
      autoHideDuration={
        isError ? ERROR_NOTIFICATION_DURATION : NOTIFICATION_DURATION
      }
      {...notificationOptions}
      TransitionProps={{
        onExited: handleExited,
      }}
      onClose={(event, reason) => {
        if (notificationOptions?.onClose && event && reason) {
          notificationOptions?.onClose(event, reason);
        }
        if (reason === 'clickaway') {
          return;
        }
        setOpen(false);
      }}
    >
      <Alert severity={getAlertSeverity(notificationOptions?.type)}>
        {notificationOptions?.message || ''}
      </Alert>
    </MuiSnackbar>
  );
};

export default Snackbar;
